import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import useAppContext from 'flow/hooks/useAppContext';
import { useModal } from 'flow/contexts/ModalProvider';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';

interface Props {
    contact?: TypedMap<ContactFull>;
    contactIsSaved: boolean;
    inPanel: boolean;
}

/**
 * The "Contact details" header action: edit a saved, editable contact, or
 * create one from an unsaved visitor. A saved contact we're not allowed to
 * edit (e.g. one owned by another customer — `editable` is false) gets no
 * button at all, matching the contact-card pattern elsewhere. The tooltip
 * text is also the button's name (IconButton makes it the aria-label).
 */
const ContactCardActionButton = ({
    contact,
    contactIsSaved,
    inPanel,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const canEditContact = contactIsSaved && !!contact?.get('editable');

    if (!canEditContact && contactIsSaved) {
        return null;
    }

    const iconKey: IconKey = canEditContact ? 'edit' : 'person_add';
    const labelKey = canEditContact
        ? 'Tooltip.Nav.Title.Edit'
        : 'Colleagues.Create.External.Title';
    const onClick = () => {
        if (canEditContact) {
            openModal('editContact', { contact });
            return;
        }
        // The ticket's contact (the stored one, else the visitor details)
        // prefills the new contact.
        openModal('createContact', {
            contact,
            countryCode: ownCountryCode,
        });
        dispatch(
            segmentTrack(TICKET_STRINGS.OPENED_CREATE_CONTACT_MODAL, {
                component: 'ticket page',
            }),
        );
    };

    return (
        <IconButton
            type="button"
            onClick={onClick}
            tooltipLabel={i18next.t(labelKey)}
            className={twMerge(
                'hover:darken-10 flex shrink-0 items-center rounded-xl p-2 text-gray-600',
                inPanel ? 'bg-gray-300' : 'bg-gray-200',
            )}
        >
            <Icon iconKey={iconKey} size="1.5rem" />
        </IconButton>
    );
};

export default React.memo(ContactCardActionButton);
